import React, { useState } from 'react';
import { Review } from '../types/Review';
import '../styles/ReviewDetailModal.css';

interface ReviewDetailModalProps {
  review: Review;
  onClose: () => void;
  onUpdate?: (id: string, updates: Partial<Review>) => void;
}

const ReviewDetailModal: React.FC<ReviewDetailModalProps> = ({ review, onClose, onUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedReview, setEditedReview] = useState<Partial<Review>>({
    ai_summary: review.ai_summary || '',
    suggested_response: review.suggested_response || '',
    response_priority: review.response_priority || 'medium',
    sentiment: review.sentiment || 'neutral'
  });
  // Helper functions to handle field name variations
  const getReviewerName = (review: Review) => {
    return review.reviewer_name || review.reviewerName || 'Anonymous';
  };

  const getReviewText = (review: Review) => {
    return review.review || review.reviewBody || '';
  };

  const getRating = (review: Review) => {
    return review.rating || review.originalRating || 0;
  };

  const getTimestamp = (review: Review) => {
    return review.timestamp || review.reviewDate || review.createdAt || '';
  };

  const getVerifiedPurchase = (review: Review) => {
    return review.verified_purchase || review.verified || review.metadata?.verified_buyer || false;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const renderStars = (rating: number) => {
    return '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating));
  };

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return '#10b981';
      case 'negative': return '#ef4444';
      case 'neutral': return '#6b7280';
      case 'mixed': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Review Details</h2>
          <div className="modal-actions">
            {onUpdate && !isEditMode && (
              <button className="modal-edit-btn" onClick={() => setIsEditMode(true)}>
                Edit
              </button>
            )}
            {isEditMode && (
              <>
                <button 
                  className="modal-save-btn" 
                  onClick={() => {
                    onUpdate(review.id, editedReview);
                    setIsEditMode(false);
                  }}
                >
                  Save
                </button>
                <button 
                  className="modal-cancel-btn" 
                  onClick={() => {
                    setIsEditMode(false);
                    setEditedReview({
                      ai_summary: review.ai_summary || '',
                      suggested_response: review.suggested_response || '',
                      response_priority: review.response_priority || 'medium',
                      sentiment: review.sentiment || 'neutral'
                    });
                  }}
                >
                  Cancel
                </button>
              </>
            )}
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
        </div>
        
        <div className="modal-body">
          {/* Review Header */}
          <div className="review-header-section">
            <div className="reviewer-info">
              <h3>{getReviewerName(review)} • {formatDate(getTimestamp(review))}</h3>
            </div>
            <div className="review-overview">
              <div className="overview-grid">
                <div className="overview-item">
                  <span className="overview-label">PRODUCT</span>
                  <span className="overview-value">{review.title || review.productTitle || 'Product'}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">RATING</span>
                  <span className="overview-value stars">{renderStars(getRating(review))}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">SENTIMENT</span>
                  {isEditMode ? (
                    <select 
                      className="edit-select"
                      value={editedReview.sentiment || 'neutral'}
                      onChange={(e) => setEditedReview({...editedReview, sentiment: e.target.value})}
                    >
                      <option value="positive">Positive</option>
                      <option value="negative">Negative</option>
                      <option value="neutral">Neutral</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  ) : (
                    <span 
                      className="overview-value sentiment"
                      style={{ color: getSentimentColor(review.sentiment), textTransform: 'capitalize' }}
                    >
                      {review.sentiment || 'Neutral'}
                    </span>
                  )}
                </div>
              </div>
              <div className="overview-grid">
                <div className="overview-item">
                  <span className="overview-label">REVIEW ID</span>
                  <span className="overview-value">#{review.id?.slice(-8) || 'N/A'}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">VERIFIED BUYER</span>
                  <span className="overview-value">{getVerifiedPurchase(review) ? '✓ Yes' : 'No'}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">COUNTRY</span>
                  <span className="overview-value">{review.metadata?.country_code || 'US'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Review Text - expand across modal width */}
          <div className="review-content-section">
            <h3>{review.title || 'Review'}</h3>
            <div className="review-text highlighted" style={{ width: '100%' }}>
              {getReviewText(review)}
            </div>
          </div>

          {/* Review Nuggets/Highlights */}
          {(review.highlights && review.highlights.length > 0) && (
            <div className="nuggets-section">
              <h3>Key Highlights from This Review ({review.highlights.length})</h3>
              <div className="nuggets-grid">
                {review.highlights.map((highlight, index) => {
                  const getAngleColor = (angle: string) => {
                    const normalizedAngle = angle?.toLowerCase();
                    
                    switch (normalizedAngle) {
                      case 'benefit':
                      case 'product_benefit':
                        return '#3b82f6'; // Blue
                      case 'emotion':
                      case 'emotional_impact':
                      case 'emotional_attachment':
                        return '#f59e0b'; // Amber
                      case 'problem_solved':
                      case 'problemsolved':
                        return '#10b981'; // Green
                      case 'unique_feature':
                      case 'uniquefeature':
                        return '#8b5cf6'; // Purple
                      case 'transformation':
                      case 'transformation_testimonial':
                        return '#ef4444'; // Red
                      case 'extreme_performance_testimonial':
                        return '#14b8a6'; // Teal
                      default:
                        return '#6b7280'; // Gray
                    }
                  };

                  const getAngleLabel = (angle: string) => {
                    const normalizedAngle = angle?.toLowerCase();
                    
                    switch (normalizedAngle) {
                      case 'benefit':
                      case 'product_benefit':
                      case 'benefit_confirmation':
                        return 'BENEFIT';
                      case 'emotion':
                      case 'emotional_impact':
                      case 'emotional_attachment':
                        return 'EMOTION';
                      case 'problem_solved':
                      case 'problemsolved':
                        return 'PROBLEM SOLVED';
                      case 'unique_feature':
                      case 'uniquefeature':
                        return 'UNIQUE FEATURE';
                      case 'transformation':
                      case 'transformation_testimonial':
                        return 'TRANSFORMATION';
                      case 'superlative_comfort':
                        return 'COMFORT';
                      case 'superlative_endorsement':
                        return 'ENDORSEMENT';
                      case 'gift_success':
                      case 'gift_recommendation':
                        return 'GIFT SUCCESS';
                      case 'repeat_purchase_intent':
                        return 'REPEAT PURCHASE';
                      case 'extreme_performance_testimonial':
                        return 'PERFORMANCE';
                      default:
                        // Format the angle text: replace underscores with spaces and capitalize
                        if (angle) {
                          return angle.replace(/_/g, ' ').toUpperCase();
                        }
                        return 'HIGHLIGHT';
                    }
                  };

                  const renderQualityStars = (quality: number) => {
                    return '★'.repeat(Math.min(5, Math.max(1, quality)));
                  };

                  return (
                    <div key={index} className="nugget-card">
                      <div className="nugget-header">
                        <div className="nugget-badges">
                          {/* Show universal category as primary badge */}
                          <span 
                            className="nugget-badge primary"
                            style={{ backgroundColor: getAngleColor(highlight.category || highlight.angle) }}
                          >
                            {highlight.category || getAngleLabel(highlight.angle)}
                          </span>
                          {/* Show specific angle as secondary badge if different */}
                          {highlight.category && highlight.angle && highlight.category !== highlight.angle && (
                            <span className="nugget-badge secondary">
                              {getAngleLabel(highlight.angle)}
                            </span>
                          )}
                        </div>
                        <span className="nugget-quality">
                          {renderQualityStars(highlight.quality || 3)}
                        </span>
                      </div>
                      <div className="nugget-text">
                        "{highlight.text}"
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* LLM Analysis Insights */}
          {review.metadata?.impact_score && (
            <div className="llm-insights-section">
              <h3>LLM Analysis Insights</h3>
              <div className="insights-grid">
                <div className="insight-card">
                  <span className="insight-label">Marketing Impact Score</span>
                  <div className="impact-score">
                    <div className="score-bar">
                      <div 
                        className="score-fill" 
                        style={{ width: `${review.metadata.impact_score}%` }}
                      ></div>
                    </div>
                    <span className="score-value">{review.metadata.impact_score}/100</span>
                  </div>
                </div>
                
                {review.metadata.emotional_intensity && (
                  <div className="insight-card">
                    <span className="insight-label">Emotional Intensity</span>
                    <span className={`intensity-badge intensity-${review.metadata.emotional_intensity}`}>
                      {review.metadata.emotional_intensity.toUpperCase()}
                    </span>
                  </div>
                )}
                
                {review.metadata.priority && (
                  <div className="insight-card">
                    <span className="insight-label">Marketing Priority</span>
                    <span className={`priority-badge priority-${review.metadata.priority.toLowerCase()}`}>
                      {review.metadata.priority}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transformation Story */}
          {review.transformation_story?.before && (
            <div className="transformation-section">
              <h3>🔄 Transformation Story</h3>
              <div className="transformation-flow">
                <div className="transformation-before">
                  <span className="transformation-label">BEFORE</span>
                  <p className="transformation-text">{review.transformation_story.before}</p>
                </div>
                <div className="transformation-arrow">→</div>
                <div className="transformation-after">
                  <span className="transformation-label">AFTER</span>
                  <p className="transformation-text">{review.transformation_story.after}</p>
                </div>
              </div>
              {review.transformation_story.timeframe && (
                <div className="transformation-timeframe">
                  <span className="timeframe-label">Timeframe:</span>
                  <span className="timeframe-value">{review.transformation_story.timeframe}</span>
                </div>
              )}
            </div>
          )}

          {/* Professional Context */}
          {review.metadata?.profession && (
            <div className="professional-section">
              <h3>👔 Professional Context</h3>
              <div className="professional-info">
                <div className="profession-badge">
                  {review.metadata.profession}
                </div>
                {review.metadata.professional_context && (
                  <p className="professional-context">{review.metadata.professional_context}</p>
                )}
              </div>
            </div>
          )}

          {(review.ai_summary || isEditMode) && (
            <div className="ai-summary">
              <h3>📝 AI Summary</h3>
              {isEditMode ? (
                <textarea
                  className="edit-textarea"
                  value={editedReview.ai_summary || ''}
                  onChange={(e) => setEditedReview({...editedReview, ai_summary: e.target.value})}
                  placeholder="Add AI summary..."
                  rows={4}
                />
              ) : (
                <p>{review.ai_summary}</p>
              )}
            </div>
          )}

          {review.key_themes && review.key_themes.length > 0 && (
            <div className="key-themes">
              <h3>🏷️ Visceral Phrases & Emotional Language</h3>
              <div className="theme-tags">
                {review.key_themes.map((theme, index) => (
                  <span key={index} className="visceral-phrase-tag">"{theme}"</span>
                ))}
              </div>
              <p className="section-description">
                Powerful emotional phrases that resonate with customers and can be used in marketing copy.
              </p>
            </div>
          )}

          {/* Core Message */}
          {review.metadata?.core_message && (
            <div className="core-message-section">
              <h3>💡 Core Message</h3>
              <div className="core-message-card">
                <p className="core-message-text">"{review.metadata.core_message}"</p>
                <span className="core-message-label">The single most powerful message in this review</span>
              </div>
            </div>
          )}

          {/* Marketing Headlines */}
          {review.metadata?.headline_options && review.metadata.headline_options.length > 0 && (
            <div className="headlines-section">
              <h3>📢 Marketing Headlines</h3>
              <div className="headlines-list">
                {review.metadata.headline_options.map((headline, index) => (
                  <div key={index} className="headline-item">
                    <span className="headline-number">{index + 1}</span>
                    <span className="headline-text">"{headline}"</span>
                  </div>
                ))}
              </div>
              <p className="section-description">
                AI-generated headlines based on this review's content and emotional impact.
              </p>
            </div>
          )}

          {review.actionable_insights && review.actionable_insights.length > 0 && (
            <div className="actionable-insights">
              <h3>Actionable Insights</h3>
              <ul>
                {review.actionable_insights.map((insight, index) => (
                  <li key={index}>{insight}</li>
                ))}
              </ul>
            </div>
          )}

          {review.product_issues && review.product_issues.length > 0 && (
            <div className="product-issues">
              <h3>Product Issues</h3>
              <ul>
                {review.product_issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {review.positive_aspects && review.positive_aspects.length > 0 && (
            <div className="positive-aspects">
              <h3>Positive Aspects</h3>
              <ul>
                {review.positive_aspects.map((aspect, index) => (
                  <li key={index}>{aspect}</li>
                ))}
              </ul>
            </div>
          )}

          {(review.suggested_response || isEditMode) && (
            <div className="suggested-response">
              <h3>💬 AI-Generated Response</h3>
              <div className="response-context">
                <div className="response-metadata">
                  {review.response_priority && (
                    <span className={`response-priority-badge priority-${review.response_priority}`}>
                      {review.response_priority.toUpperCase()} PRIORITY
                    </span>
                  )}
                  {review.metadata?.processed_at && (
                    <span className="processed-date">
                      Generated: {new Date(review.metadata.processed_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              {isEditMode ? (
                <textarea
                  className="edit-textarea response"
                  value={editedReview.suggested_response || ''}
                  onChange={(e) => setEditedReview({...editedReview, suggested_response: e.target.value})}
                  placeholder="Add suggested response..."
                  rows={6}
                />
              ) : (
                <div className="response-text">
                  <div className="response-content">
                    {review.suggested_response}
                  </div>
                  <div className="response-tips">
                    <strong>Usage Tips:</strong> This response is tailored to the review's emotional tone and key themes. 
                    Copy and personalize as needed for your brand voice.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewDetailModal;